import React, {useRef, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';

import {Group} from 'src/design/atoms/Group';
import type {TaskDefinitionRequest} from 'src/api/ProactApiClient';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
  TaskDefinitionStateEnum,
  TaskTypeEnum,
} from 'src/api/ProactApiClient';
import {useCreateTaskDefinitionMutation} from 'src/redux/queries/taskDefinition';
import {SaveButton} from 'src/design/molecules/SaveButton';
import {FormTransitionBar} from 'src/app/forms/FormTransitionBar';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {TaskFeatureEditor} from 'src/app/tasks/TaskFeatureEditor';
import {buildCreateTaskRequest} from 'src/app/tasks/taskRequestTransport';
import {
  GetModuleEnumFromTaskType,
  GetUrlSectionFromTaskType,
  TaskPageTitleDict,
} from 'src/app/tasks/taskHelpers';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {ApiEndpoints} from 'src/shared/constants/apiEndpoints';
import {useTaskBreadcrumbs} from 'src/shared/hooks/useTaskBreadcrumbs';
import {useStateMachine} from 'src/shared/hooks/api/useStateMachine';
import {capitalize} from 'src/shared/utils/formatters';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IStateMachine} from 'src/types/StateMachine';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface CreateTaskPageProps {
  taskType: TaskTypeEnum;
  featureFlag: FeatureFlagEnum;
}

const CreateTaskPage: React.FC<CreateTaskPageProps> = ({
  taskType,
  featureFlag,
}) => {
  useSetDocumentTitle(capitalize(GetUrlSectionFromTaskType(taskType)));
  const module = GetModuleEnumFromTaskType(taskType);

  const [searchParams] = useSearchParams();
  const formId = searchParams.get('formId');

  const {setAlert} = useAlert();
  const navigate = useAppNavigate();
  const [isSavePending, setIsSavePending] = useState<boolean>(false);

  const [createTaskDefinition] = useCreateTaskDefinitionMutation();

  const taskId = useRef<number | null>(null);
  const transitionUrl = `task-definition/${taskId.current}/transition`;

  const onSubmitted = () =>
    navigate(
      PathUtility.TaskView(taskType, String(taskId.current), 'content'),
      {
        replace: true,
      }
    );

  const breadcrumbs = useTaskBreadcrumbs({
    taskType,
    mode: 'create',
  });

  const [machineBase] = useStateMachine({
    url: ApiEndpoints.getTaskDefinitionStateMachine,
  });
  const machine = machineBase
    ? ({
        states: machineBase.states,
        transitions: machineBase.transitions.map(t => ({
          ...t,
          title: `Save & ${t.title}`,
        })),
      } as IStateMachine)
    : undefined;

  return (
    <TaskFeatureEditor taskType={taskType}>
      {({formApi, form, getFeatureFields}) => {
        const submit = async (request?: TaskDefinitionRequest) => {
          if (!(await formApi.validateForm())) {
            return {continue: false};
          }
          setIsSavePending(true);
          const body = buildCreateTaskRequest({
            taskType,
            featureFields: getFeatureFields(),
            sourceFormId: formId ? +formId : undefined,
            transitionFields: request,
          });
          try {
            const resp = await createTaskDefinition(body).unwrap();
            setAlert(
              `${TaskPageTitleDict[taskType].singular} was successfully created`,
              'success'
            );
            taskId.current = resp.id;
            return {
              continue: true,
              overrideTransitionBaseUrl: `task-definition/${resp.id}/transition`,
            };
          } catch (error) {
            setAlert(parseErrorMessage(error), 'error');
            setIsSavePending(false);
            return {continue: false};
          }
        };
        const handleSave = async () => {
          const result = await submit();
          if (result.continue) onSubmitted();
        };
        const handleTransitioning = () =>
          submit({skipOnUpdatedSideEffect: true});

        return (
          <ContentWrapper featureFlag={featureFlag}>
            <BackBar
              fallbackTo={PathUtility.TasksDashboard(taskType, 'manage')}
              breadcrumbs={breadcrumbs}
            />
            <PageHeader>
              <PageHeader.Title>
                {`New ${TaskPageTitleDict[taskType].singular}`}
              </PageHeader.Title>
              <PageHeader.Actions>
                <PageHeader.Action
                  variant="save"
                  loading={isSavePending}
                  onClick={handleSave}>
                  Save
                </PageHeader.Action>
              </PageHeader.Actions>
            </PageHeader>
            {form}
            <Group align="start">
              {machine ? (
                <FormTransitionBar
                  preTransitionButtonNodes={[
                    <SaveButton
                      loading={isSavePending}
                      onClick={handleSave}
                      mr="md"
                    />,
                  ]}
                  onTransitioning={handleTransitioning}
                  module={module}
                  stateMachine={machine}
                  stateId={TaskDefinitionStateEnum.Draft}
                  transitionBaseUrl={transitionUrl}
                  onRefreshTransition={onSubmitted}
                  invalidateTagsOnComplete={[
                    'TaskDefinitions',
                    'TaskAssignments',
                  ]}
                />
              ) : (
                <SaveButton loading={isSavePending} onClick={handleSave} />
              )}
            </Group>
          </ContentWrapper>
        );
      }}
    </TaskFeatureEditor>
  );
};

// Register the routes with the new system
[
  {
    type: TaskTypeEnum.Approval,
    featureFlag: FeatureFlagEnum.Approvals,
    module: ModuleEnum.Approvals,
  },
  {
    type: TaskTypeEnum.ToolboxTalk,
    featureFlag: FeatureFlagEnum.ToolboxTalks,
    module: ModuleEnum.ToolboxTalks,
  },
  {
    type: TaskTypeEnum.Form,
    featureFlag: FeatureFlagEnum.Forms,
    module: ModuleEnum.AssignedForms,
  },
].forEach(item => {
  routeRegistry.registerPageRoute({
    backNavigation: 'form',
    path: PathUtility.TaskCreate(item.type),
    element: (
      <CreateTaskPage taskType={item.type} featureFlag={item.featureFlag} />
    ),
    module: item.module,
    operation: PermissionOperationEnum.Create,
    isPrivate: true,
  });
});

export default CreateTaskPage;
